import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaStar, FaQuoteLeft } from 'react-icons/fa';

// Styled Components
const TestimonialsSection = styled.section`
  padding: 5rem 0;
  background-color: #f8fafc;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #f59e0b;
  }
`;

const TestimonialsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SliderWrapper = styled.div`
  .slick-dots li button:before {
    font-size: 12px;
    color: #1e3a8a;
  }
  
  .slick-dots li.slick-active button:before {
    color: #1e3a8a;
  }
`;

const TestimonialCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 2rem;
  margin: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const QuoteIcon = styled.div`
  font-size: 2rem;
  color: #f59e0b;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const TestimonialText = styled.p`
  font-style: italic;
  color: #4b5563;
  margin-bottom: 1.5rem;
  flex-grow: 1;
  line-height: 1.6;
`;

const ClientInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ClientImage = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 1rem;
  border: 3px solid #f59e0b;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ClientName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #1f2937;
`;

const Rating = styled.div`
  display: flex;
  justify-content: center;
  color: #f59e0b;
  margin-bottom: 0.5rem;
  
  svg {
    margin: 0 2px;
  }
`;

// Sample testimonials - replace with Firebase data in production
const sampleTestimonials = [
  {
    id: 1,
    name: 'Rahul Sharma',
    image: 'https://randomuser.me/api/portraits/men/32.jpg',
    rating: 5,
    text: 'Our stay at Lakeside Haven was absolutely magical. The views of Wular Lake from our room were breathtaking, and the staff went above and beyond to make our anniversary special.'
  },
  {
    id: 2,
    name: 'Priya Patel',
    image: 'https://randomuser.me/api/portraits/women/44.jpg',
    rating: 5,
    text: 'The perfect family getaway! Our kids loved the play area and the shikara ride. The rooms were spacious and clean, and the food was delicious. We will definitely be back!'
  },
  {
    id: 3,
    name: 'Amit Verma',
    image: 'https://randomuser.me/api/portraits/men/62.jpg',
    rating: 4,
    text: 'We hosted our company retreat here and it was a great success. The conference facilities were excellent and the team-building activities like trekking and bonfire were highlights for everyone.'
  },
  {
    id: 4,
    name: 'Sneha Gupta',
    image: 'https://randomuser.me/api/portraits/women/26.jpg',
    rating: 5,
    text: 'The lakefront dining experience was unforgettable. Watching the sunset over Wular Lake while enjoying Kashmiri cuisine was the highlight of our trip. Highly recommended!'
  }
];

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch testimonials from Firebase
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const testimonialCollection = collection(db, 'testimonials');
        const testimonialSnapshot = await getDocs(testimonialCollection);
        
        if (!testimonialSnapshot.empty) {
          const testimonialList = testimonialSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setTestimonials(testimonialList);
        } else {
          // Use sample data if no testimonials in Firebase
          setTestimonials(sampleTestimonials);
        }
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        // Fallback to sample data
        setTestimonials(sampleTestimonials);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTestimonials();
  }, []);
  
  // Settings for the slider
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };
  
  // Render stars based on rating
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <FaStar key={i} color={i < rating ? '#f59e0b' : '#d1d5db'} />
      );
    }
    return stars;
  };
  
  return (
    <TestimonialsSection id="testimonials">
      <SectionTitle>What Our Guests Say</SectionTitle>
      <TestimonialsContainer>
        {loading ? (
          <p>Loading testimonials...</p>
        ) : (
          <SliderWrapper>
            <Slider {...settings}>
              {testimonials.map((testimonial) => (
                <div key={testimonial.id}>
                  <TestimonialCard>
                    <QuoteIcon>
                      <FaQuoteLeft />
                    </QuoteIcon>
                    <TestimonialText>{testimonial.text}</TestimonialText>
                    <ClientInfo>
                      <ClientImage>
                        <img src={testimonial.image} alt={testimonial.name} />
                      </ClientImage>
                      <ClientName>{testimonial.name}</ClientName>
                      <Rating>
                        {renderStars(testimonial.rating)}
                      </Rating>
                    </ClientInfo>
                  </TestimonialCard>
                </div>
              ))}
            </Slider>
          </SliderWrapper>
        )}
      </TestimonialsContainer>
    </TestimonialsSection>
  );
};

export default Testimonials;
